// ============================================
// Extraction Output JSON Schema Validation
//
// Validates all model outputs against the ExtractionOutput schema
// before further processing. Schema mismatches trigger immediate
// failover to the next available model.
//
// K=TOP: No malformed model output reaches the patient.
// If the model returns garbage, we fail over — never guess.
// ============================================

/**
 * ExtractionOutput — the canonical schema every model must produce.
 * This is the contract: an array of biomarker objects with required fields.
 * Both client-side (BiomarkerResult) and server-side (ExtractedBiomarker)
 * extraction outputs are validated against this shape.
 */
export interface ExtractionOutput {
  /** Must be an array of biomarker extraction items */
  items: ExtractionOutputItem[];
}

export interface ExtractionOutputItem {
  /** Biomarker name — required, non-empty string */
  name: string;
  /** Numeric value — required, must be number or null */
  value: number | null;
  /** Unit of measurement — required string */
  unit: string;
}

// ── Schema validation errors ──

export interface SchemaValidationError {
  field: string;
  expected: string;
  received: string;
  itemIndex?: number;
}

export interface ExtractionValidationResult {
  valid: boolean;
  errors: SchemaValidationError[];
  /** Model that produced the output */
  modelId: string;
  /** Truncated snippet for logging (no PHI — values only, no names) */
  outputSnippet: string;
}

// ── Validation logic ──

/**
 * Validate a single extraction item against the ExtractionOutputItem schema.
 * Returns errors found (empty array = valid).
 */
function validateItem(item: unknown, index: number): SchemaValidationError[] {
  const errors: SchemaValidationError[] = [];

  if (item === null || item === undefined || typeof item !== 'object') {
    errors.push({
      field: 'item',
      expected: 'object',
      received: typeof item,
      itemIndex: index,
    });
    return errors;
  }

  const obj = item as Record<string, unknown>;

  // Name field: must exist and be a non-empty string
  if (typeof obj.name !== 'string' && typeof obj.rawName !== 'string' && typeof obj.biomarker !== 'string') {
    errors.push({
      field: 'name',
      expected: 'non-empty string (name | rawName | biomarker)',
      received: `name=${typeof obj.name}, rawName=${typeof obj.rawName}, biomarker=${typeof obj.biomarker}`,
      itemIndex: index,
    });
  } else {
    const nameVal = String(obj.name || obj.rawName || obj.biomarker || '');
    if (nameVal.trim().length === 0) {
      errors.push({
        field: 'name',
        expected: 'non-empty string',
        received: 'empty string',
        itemIndex: index,
      });
    }
  }

  // Value field: must be number or null
  if (obj.value !== null && obj.value !== undefined && typeof obj.value !== 'number') {
    // Allow string numbers that can be parsed
    const parsed = Number(obj.value);
    if (isNaN(parsed)) {
      errors.push({
        field: 'value',
        expected: 'number | null',
        received: `${typeof obj.value}: ${String(obj.value).slice(0, 30)}`,
        itemIndex: index,
      });
    }
  }

  // Unit field: must be a string
  if (obj.unit !== undefined && typeof obj.unit !== 'string') {
    errors.push({
      field: 'unit',
      expected: 'string',
      received: typeof obj.unit,
      itemIndex: index,
    });
  }

  return errors;
}

/**
 * Create a safe output snippet for logging — no PHI.
 * Shows structure shape, not patient data.
 */
function createSafeSnippet(output: unknown): string {
  try {
    if (output === null || output === undefined) return 'null';
    if (typeof output === 'string') return `string(${output.length} chars)`;
    if (!Array.isArray(output)) return `non-array: ${typeof output}`;

    const count = output.length;
    if (count === 0) return 'empty array []';

    // Show field keys of first item only — no values (could contain PHI)
    const first = output[0];
    const keys = first && typeof first === 'object' ? Object.keys(first).join(', ') : typeof first;
    return `array[${count}] first_keys: {${keys}}`;
  } catch {
    return 'unparseable';
  }
}

/**
 * Validate a parsed extraction output array against the ExtractionOutput schema.
 *
 * @param output - The parsed model output (should be an array of biomarker objects)
 * @param modelId - The model that produced this output (for logging)
 * @returns Validation result with errors if any
 */
export function validateExtractionOutput(
  output: unknown,
  modelId: string,
): ExtractionValidationResult {
  const snippet = createSafeSnippet(output);
  const errors: SchemaValidationError[] = [];

  // Must be an array
  if (!Array.isArray(output)) {
    errors.push({
      field: 'root',
      expected: 'array',
      received: typeof output,
    });
    const result: ExtractionValidationResult = { valid: false, errors, modelId, outputSnippet: snippet };
    logSchemaMismatch(result);
    return result;
  }

  // Must not be empty
  if (output.length === 0) {
    errors.push({
      field: 'root',
      expected: 'non-empty array',
      received: 'empty array',
    });
    const result: ExtractionValidationResult = { valid: false, errors, modelId, outputSnippet: snippet };
    logSchemaMismatch(result);
    return result;
  }

  // Validate each item
  for (let i = 0; i < output.length; i++) {
    const itemErrors = validateItem(output[i], i);
    errors.push(...itemErrors);
  }

  const result: ExtractionValidationResult = {
    valid: errors.length === 0,
    errors,
    modelId,
    outputSnippet: snippet,
  };

  if (!result.valid) {
    logSchemaMismatch(result);
  }

  return result;
}

/**
 * Log schema mismatch events — c3 requirement.
 * Logs modelId, error details, and a safe output snippet (no PHI).
 */
function logSchemaMismatch(result: ExtractionValidationResult): void {
  const errorSummary = result.errors
    .slice(0, 5) // Cap at 5 errors to avoid log spam
    .map((e) => {
      const idx = e.itemIndex !== undefined ? `[${e.itemIndex}]` : '';
      return `${e.field}${idx}: expected ${e.expected}, got ${e.received}`;
    })
    .join('; ');

  console.error(
    `[ExtractionValidation] SCHEMA_MISMATCH | model=${result.modelId} | ` +
    `errors=${result.errors.length} | snippet=${result.outputSnippet} | ` +
    `details: ${errorSummary}`,
  );
}

/**
 * Validates a raw JSON string from a model, parsing it first.
 * Returns the parsed array on success, or throws SchemaValidationError on failure.
 *
 * Use this in extraction pipelines to gate model output before processing.
 */
export function parseAndValidateExtractionOutput(
  rawResponse: string,
  modelId: string,
): Record<string, unknown>[] {
  // Strip markdown fences
  let cleaned = rawResponse.trim();
  if (cleaned.startsWith('```')) {
    cleaned = cleaned.replace(/^```(?:json)?\n?/, '').replace(/\n?```$/, '');
  }

  // Find JSON content
  const start = cleaned.search(/[\[{]/);
  if (start === -1) {
    const result: ExtractionValidationResult = {
      valid: false,
      errors: [{ field: 'root', expected: 'JSON array or object', received: 'no JSON found' }],
      modelId,
      outputSnippet: `string(${rawResponse.length} chars), no JSON`,
    };
    logSchemaMismatch(result);
    throw new ExtractionSchemaError(result);
  }

  let parsed: unknown;
  try {
    // Extract balanced JSON
    const open = cleaned[start];
    const close = open === '[' ? ']' : '}';
    let depth = 0;
    let end = start;
    for (let i = start; i < cleaned.length; i++) {
      if (cleaned[i] === open) depth++;
      if (cleaned[i] === close) depth--;
      if (depth === 0) {
        end = i;
        break;
      }
    }
    parsed = JSON.parse(cleaned.slice(start, end + 1));
  } catch (e) {
    const result: ExtractionValidationResult = {
      valid: false,
      errors: [{ field: 'root', expected: 'valid JSON', received: `parse error: ${(e as Error).message}` }],
      modelId,
      outputSnippet: `string(${rawResponse.length} chars)`,
    };
    logSchemaMismatch(result);
    throw new ExtractionSchemaError(result);
  }

  // Normalize to array
  const items = Array.isArray(parsed) ? parsed : [parsed];

  // Validate against schema
  const validation = validateExtractionOutput(items, modelId);
  if (!validation.valid) {
    throw new ExtractionSchemaError(validation);
  }

  return items as Record<string, unknown>[];
}

/**
 * Error thrown when model output fails schema validation.
 * Carries the full validation result for failover logic to inspect.
 */
export class ExtractionSchemaError extends Error {
  public readonly validation: ExtractionValidationResult;

  constructor(validation: ExtractionValidationResult) {
    const errorCount = validation.errors.length;
    super(
      `[ExtractionValidation] Schema mismatch from ${validation.modelId}: ` +
      `${errorCount} error(s) — ${validation.outputSnippet}`,
    );
    this.name = 'ExtractionSchemaError';
    this.validation = validation;
  }
}
